// QR スキャン受付画面 (要件 §7-4)
import { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { api } from "../../api";
import { SectionHeading } from "../../components/ui";

interface EventRow {
  id: string;
  title: string;
  status: string;
  hasSocial?: number;
}

type CheckinType = "event" | "social";

export function AdminCheckinPage() {
  const [events, setEvents] = useState<EventRow[]>([]);
  const [eventId, setEventId] = useState<string>("");
  const [checkinType, setCheckinType] = useState<CheckinType>("event");
  const [scanning, setScanning] = useState(false);
  const [log, setLog] = useState<string[]>([]);
  const [manualMemberId, setManualMemberId] = useState("");
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const lastScannedRef = useRef<string>("");
  const scanCooldownRef = useRef(false);
  const containerId = "qr-scanner-container";
  // 結果オーバーレイ (受付完了/エラーを画面中央に大きく表示)
  const [flash, setFlash] = useState<
    { kind: "success" | "error"; title: string; sub: string } | null
  >(null);
  const flashTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showFlash = (kind: "success" | "error", title: string, sub: string) => {
    setFlash({ kind, title, sub });
    if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
    flashTimerRef.current = setTimeout(() => setFlash(null), 2500);
    // 音 + バイブで即時フィードバック
    try {
      const AudioCtx =
        window.AudioContext ||
        (window as unknown as { webkitAudioContext?: typeof AudioContext })
          .webkitAudioContext;
      if (AudioCtx) {
        const ctx = new AudioCtx();
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.frequency.value = kind === "success" ? 880 : 220;
        gain.gain.value = 0.15;
        osc.start();
        osc.stop(ctx.currentTime + (kind === "success" ? 0.15 : 0.4));
      }
    } catch { /* 音は出せなくても続行 */ }
    if (navigator.vibrate) navigator.vibrate(kind === "success" ? 120 : [80, 60, 80]);
  };

  useEffect(() => {
    api
      .get<{ items: EventRow[] }>("/admin/events")
      .then((r) => setEvents(r.items));
  }, []);

  useEffect(
    () => () => {
      scannerRef.current?.stop().catch(() => {});
    },
    [],
  );

  const selectedEvent = events.find((e) => e.id === eventId);
  const hasSocial = selectedEvent?.hasSocial === 1;
  const typeLabel = checkinType === "social" ? "懇親会" : "イベント";

  const append = (line: string) =>
    setLog((l) => [`${new Date().toLocaleTimeString("ja-JP", { timeZone: "Asia/Tokyo" })} ${line}`, ...l].slice(0, 50));

  const start = async () => {
    if (!eventId) {
      append("✗ イベントを選んでください");
      return;
    }
    setScanning(true);
    const scanner = new Html5Qrcode(containerId);
    scannerRef.current = scanner;
    try {
      await scanner.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: 240 },
        async (decoded) => {
          if (decoded === lastScannedRef.current || scanCooldownRef.current) return;
          lastScannedRef.current = decoded;
          scanCooldownRef.current = true;
          setTimeout(() => { scanCooldownRef.current = false; }, 3000);
          try {
            const r = await api.post<{ ok: boolean; label?: string; displayName?: string; memberNo?: string }>("/admin/checkin/scan", {
              eventId,
              qrToken: decoded,
              checkinType,
            });
            if (r.ok) {
              const name = r.displayName ? `${r.displayName} さん` : "";
              append(`✓ ${r.label ?? typeLabel}受付成功 ${name}`);
              showFlash("success", `${r.label ?? typeLabel}受付完了`, name || (r.memberNo ?? ""));
            }
          } catch (e) {
            const msg = (e as Error).message;
            append(`✗ ${msg}`);
            showFlash("error", "受付できません", msg);
          }
        },
        () => {},
      );
    } catch (e) {
      append(`カメラ起動失敗: ${(e as Error).message}`);
      setScanning(false);
    }
  };

  const stop = async () => {
    await scannerRef.current?.stop().catch(() => {});
    setScanning(false);
  };

  const manual = async () => {
    if (!eventId || !manualMemberId) return;
    try {
      const r = await api.post<{ ok: boolean; label?: string; displayName?: string }>("/admin/checkin/manual", {
        eventId,
        memberId: manualMemberId,
        checkinType,
      });
      const name = r.displayName ? `${r.displayName} さん` : "";
      append(`✓ ${r.label ?? typeLabel}手動受付成功 ${name}`);
      showFlash("success", `${r.label ?? typeLabel}受付完了`, name);
      setManualMemberId("");
    } catch (e) {
      const msg = (e as Error).message;
      append(`✗ ${msg}`);
      showFlash("error", "受付できません", msg);
    }
  };

  return (
    <div className="space-y-4">
      <SectionHeading level={2}>受付 (QR スキャン)</SectionHeading>
      <div>
        <label className="label">イベント</label>
        <select
          className="input max-w-md"
          value={eventId}
          onChange={(e) => { setEventId(e.target.value); setCheckinType("event"); }}
        >
          <option value="">選択してください</option>
          {events.map((e) => (
            <option key={e.id} value={e.id}>
              {e.title}
            </option>
          ))}
        </select>
      </div>

      {eventId && (
        <div className="flex gap-2 max-w-md">
          <button
            className={`flex-1 py-2 rounded-lg text-sm font-bold transition-colors ${
              checkinType === "event"
                ? "bg-tora text-white"
                : "bg-black/5 text-tora-ink/70 hover:bg-black/10"
            }`}
            onClick={() => setCheckinType("event")}
          >
            イベント受付
          </button>
          {hasSocial && (
            <button
              className={`flex-1 py-2 rounded-lg text-sm font-bold transition-colors ${
                checkinType === "social"
                  ? "bg-orange-500 text-white"
                  : "bg-black/5 text-tora-ink/70 hover:bg-black/10"
              }`}
              onClick={() => setCheckinType("social")}
            >
              懇親会受付
            </button>
          )}
        </div>
      )}

      <div className="card p-3 max-w-md">
        {checkinType === "social" && (
          <p className="text-sm font-bold text-orange-600 mb-2">懇親会モード</p>
        )}
        <div className="relative">
          <div id={containerId} className="w-full" />
          {flash && (
            <div
              className={`absolute inset-0 flex flex-col items-center justify-center text-center rounded-lg z-10 ${
                flash.kind === "success" ? "bg-green-600/95" : "bg-red-600/95"
              }`}
            >
              <span className="text-6xl">{flash.kind === "success" ? "✓" : "✕"}</span>
              <span className="text-white text-2xl font-bold mt-2">{flash.title}</span>
              {flash.sub && (
                <span className="text-white text-lg mt-1 px-3">{flash.sub}</span>
              )}
            </div>
          )}
        </div>
        {!scanning ? (
          <button className="btn-primary mt-2" onClick={start}>
            カメラ開始
          </button>
        ) : (
          <button className="btn-outline mt-2" onClick={stop}>
            停止
          </button>
        )}
      </div>

      <div className="card p-3 max-w-md">
        <h3 className="font-bold text-sm">手入力フォールバック ({typeLabel})</h3>
        <div className="flex gap-2 mt-2">
          <input
            className="input"
            placeholder="memberId"
            value={manualMemberId}
            onChange={(e) => setManualMemberId(e.target.value)}
          />
          <button className="btn-primary" onClick={manual}>
            登録
          </button>
        </div>
      </div>

      <div className="card p-3 max-w-md">
        <h3 className="font-bold text-sm">ログ</h3>
        <ul className="text-xs font-mono mt-2 space-y-0.5 max-h-60 overflow-y-auto">
          {log.map((l, i) => (
            <li key={i}>{l}</li>
          ))}
        </ul>
      </div>
    </div>
  );
}
